import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import BookingModal from '../components/BookingModal';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { GYM_VENUE } from '../config/venue';
import {
  CalendarCheck, Clock, MapPin, Phone, ShieldCheck, Award, Video, BadgeCheck,
} from 'lucide-react';

const POINTS = [
  {
    icon: BadgeCheck,
    title: 'Díjmentes és kötöttség nélküli',
    text: 'Se előleg, se szerződés — a konzultáció és az állapotfelmérés is ingyenes.',
  },
  {
    icon: Video,
    title: 'Személyesen vagy online',
    text: `A 20 perces beszélgetés mehet a ${GYM_VENUE.name}-ben, Messengeren vagy Google Meeten.`,
  },
  {
    icon: ShieldCheck,
    title: 'Okleveles sportedző',
    text: 'EKKR-MKKR 5. szintű, Europass minősítésű végzettség — nem sablonprogramok.',
  },
];

const BookingLandingPage = () => {
  const [params] = useSearchParams();
  // ?tipus=felmeres → a 60 perces állapotfelmérés van előre kiválasztva.
  const defaultDuration = params.get('tipus') === 'felmeres' ? 60 : 20;
  const [open, setOpen] = useState(true);

  useDocumentMeta({
    title: 'Időpontfoglalás | Ingyenes konzultáció — Orosz Coaching Miskolc',
    description:
      'Foglalj ingyenes konzultációt vagy állapotfelmérést Orosz Gábor okleveles sportedzőnél Miskolcon. Néhány kattintás, kötöttség nélkül.',
    canonical: 'https://oroszcoaching.hu/idopontfoglalas',
    type: 'website',
  });

  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  return (
    <div data-testid="booking-landing-page" className="min-h-screen bg-gray-950 text-white">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, #fbbf24 1px, transparent 0)',
        backgroundSize: '28px 28px',
      }} />

      <div className="relative max-w-3xl mx-auto px-5 py-12 sm:py-16">
        <Link to="/" data-testid="booking-landing-logo"
          className="inline-block text-xs font-black tracking-[0.3em] text-yellow-500 uppercase mb-10 hover:text-yellow-400 transition-colors">
          Orosz Coaching
        </Link>

        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-[11px] font-bold uppercase tracking-wider mb-6">
          <CalendarCheck size={13} /> Ingyenes időpont · Miskolc
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-5"
          style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Foglald le az <span className="text-yellow-500">ingyenes</span> időpontodat
        </h1>
        <p className="text-base sm:text-lg text-gray-300 max-w-xl mb-8 leading-relaxed">
          Válassz egy szabad napot és időpontot a naptárból — a visszaigazolást azonnal
          e-mailben küldöm. Nincs előre fizetés, nincs kötelezettség.
        </p>

        <Button data-testid="booking-landing-cta" onClick={() => setOpen(true)}
          className="h-14 px-8 bg-yellow-500 hover:bg-yellow-400 text-gray-950 text-base font-black rounded-full transition-transform hover:scale-[1.02]">
          <CalendarCheck size={18} className="mr-2" /> Időpont kiválasztása
        </Button>

        <div className="grid sm:grid-cols-3 gap-4 mt-14">
          {POINTS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="p-5 rounded-xl bg-white/[0.04] border border-white/10 backdrop-blur-sm">
              <Icon size={20} className="text-yellow-500 mb-3" />
              <div className="text-sm font-bold mb-1.5">{title}</div>
              <p className="text-[13px] text-gray-400 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-10 text-[13px] text-gray-400">
          <span className="inline-flex items-center gap-2">
            <MapPin size={14} className="text-yellow-500" /> {GYM_VENUE.fullAddress}
          </span>
          <span className="inline-flex items-center gap-2">
            <Clock size={14} className="text-yellow-500" /> 20 vagy 60 perces időpont
          </span>
          <a href="tel:+36702378299" data-testid="booking-landing-phone"
            className="inline-flex items-center gap-2 hover:text-yellow-400 transition-colors">
            <Phone size={14} className="text-yellow-500" /> +36 70 237 8299
          </a>
          <span className="inline-flex items-center gap-2">
            <Award size={14} className="text-yellow-500" /> EKKR-MKKR 5. szint
          </span>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 text-[12px] text-gray-500 flex flex-wrap gap-x-5 gap-y-2">
          <Link to="/adatkezelesi-tajekoztato" className="hover:text-gray-300">Adatkezelés</Link>
          <Link to="/impresszum" className="hover:text-gray-300">Impresszum</Link>
          <Link to="/aszf" className="hover:text-gray-300">ÁSZF</Link>
          <Link to="/" className="hover:text-gray-300">Vissza a főoldalra</Link>
        </div>
      </div>

      <BookingModal isOpen={open} onClose={() => setOpen(false)} defaultDuration={defaultDuration} />
    </div>
  );
};

export default BookingLandingPage;
